import { useRef, useState } from 'react';
import { Camera, CheckCircle2, Clock, ImagePlus, Loader2, XCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Camera as CapCamera, CameraDirection, CameraResultType, CameraSource } from '@capacitor/camera';
import Sheet from '../../shared/ui/Sheet';
import { updateDriver } from '../../shared/services/userService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { serverTimestamp } from 'firebase/firestore';
import { storage } from '../../shared/firebase';

// Camera-only capture for driver verification documents (selfie, license,
// vehicle, delivery bag) — mirrors iOS DriverDocumentCaptureView.
const STATUS = {
  approved: { label: 'Approved', sub: 'This document is verified', color: 'text-green-600', bg: 'bg-green-50', Icon: CheckCircle2 },
  pending: { label: 'Pending Review', sub: 'Our team is reviewing your photo', color: 'text-orange-500', bg: 'bg-orange-50', Icon: Clock },
  rejected: { label: 'Rejected', sub: 'Please retake a clear photo', color: 'text-red-500', bg: 'bg-red-50', Icon: XCircle },
  not_submitted: { label: 'Photo Required', sub: 'Take a photo to submit for review', color: 'text-gray-500', bg: 'bg-gray-100', Icon: Camera },
};

export default function DocumentSheet({ open, onClose, driverId, driver, doc }) {
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [localURL, setLocalURL] = useState('');

  if (!doc) return null;
  const prefix = doc.fieldPrefix;
  const imageURL = driver?.[`${prefix}URL`] || driver?.raw?.[`${prefix}URL`] || '';
  const status = (driver?.[`${prefix}Status`] || driver?.raw?.[`${prefix}Status`] || '').toLowerCase()
    || (imageURL ? 'pending' : 'not_submitted');
  const s = STATUS[status] || STATUS.not_submitted;

  const upload = async (file) => {
    if (!file || !driverId) return;
    setBusy(true);
    setError('');
    try {
      const storageRef = ref(storage, `permits/drivers/${driverId}/${doc.file}.jpg`);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      await updateDriver(driverId, {
        [`${prefix}URL`]: url,
        [`${prefix}Status`]: 'pending',
        [`${prefix}SubmittedAt`]: serverTimestamp(),
      });
      setLocalURL(url);
    } catch (err) {
      console.error('document upload', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLocalURL(URL.createObjectURL(file));
    upload(file);
  };

  // On Capacitor use the native Camera plugin — `capture` opens a file
  // chooser in the WebView. `capture="user"` targets the front camera in
  // the browser fallback for selfies.
  const takePhoto = async () => {
    if (!Capacitor.isNativePlatform()) {
      cameraRef.current?.click();
      return;
    }
    try {
      const photo = await CapCamera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        direction: doc.frontCamera ? CameraDirection.Front : CameraDirection.Rear,
        quality: 85,
      });
      if (!photo?.webPath) return;
      const res = await fetch(photo.webPath);
      const blob = await res.blob();
      setLocalURL(URL.createObjectURL(blob));
      await upload(new File([blob], `${doc.file}.jpg`, { type: 'image/jpeg' }));
    } catch (err) {
      if (err?.message && /cancel/i.test(err.message)) return;
      console.error('document camera', err);
      setError('Could not open the camera. Check camera permission in Settings.');
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={doc.title}>
      <div className="px-4 pb-6 space-y-4">
        <div className={`flex items-center gap-3 rounded-2xl p-4 ${s.bg}`}>
          <s.Icon size={24} className={s.color} />
          <div>
            <p className={`text-sm font-bold ${s.color}`}>{s.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.sub}</p>
          </div>
        </div>

        {localURL || imageURL ? (
          <img
            src={localURL || imageURL}
            alt={doc.title}
            className="w-full max-h-72 object-contain rounded-2xl bg-gray-50 border border-gray-100"
          />
        ) : (
          <div className="h-44 rounded-2xl bg-gray-100 flex flex-col items-center justify-center gap-2 text-gray-400">
            <Camera size={40} />
            <p className="text-sm font-semibold">No Photo Submitted</p>
          </div>
        )}

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture={doc.frontCamera ? 'user' : 'environment'}
          className="hidden"
          onChange={onPick}
        />
        <button
          type="button"
          onClick={takePhoto}
          disabled={busy}
          className="w-full py-3 rounded-2xl bg-[#FF6B35] text-white text-sm font-bold flex items-center justify-center gap-2 active:bg-orange-600 disabled:opacity-50"
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : <Camera size={17} />}
          {localURL || imageURL ? 'Retake Photo' : 'Take Photo'}
        </button>

        <p className="text-xs text-gray-400 text-center px-2">{doc.instructions}</p>

        {error && <p className="text-xs text-red-500 text-center">{error}</p>}
      </div>
    </Sheet>
  );
}
